/**
 * Demo Analytics Service
 * Tracks and aggregates user interactions during demo experience
 * Data is collected and sent with email signup for conversion analysis
 */

class DemoAnalyticsService {
  constructor() {
    this.sessionData = this.initializeSession();
    this.startTime = Date.now();
  }

  initializeSession() {
    return {
      demo_completion: {
        mode: null, // 'exam' or 'quiz'
        questions_answered: 0,
        total_questions: 0,
        completion_rate: 0,
        time_spent_seconds: 0,
        final_score: null
      },
      interactions: {
        hint_clicks: 0,
        ai_chat_clicks: 0,
        help_seeking_questions: [],
        cta_clicks: {
          header: 0,
          progress: 0,
          completion: 0
        },
        navigation_events: []
      },
      performance: {
        mcq_correct: 0,
        mcq_total: 0,
        mcq_accuracy: 0,
        questions_with_help: [],
        extended_questions_viewed: 0
      },
      navigation: {
        pages_visited: [],
        demo_sessions: 1,
        time_to_signup_seconds: null,
        exam_id: null
      }
    };
  }

  // Track demo mode and exam metadata
  trackDemoStart(mode, examData) {
    this.sessionData.demo_completion.mode = mode;
    this.sessionData.navigation.exam_id = examData?.exam_id;
    
    if (examData?.questions) {
      const mcqCount = examData.questions.mcq_questions?.length || 0;
      const extendedCount = examData.questions.extended_questions?.length || 0;
      this.sessionData.demo_completion.total_questions = mcqCount + extendedCount;
      this.sessionData.performance.mcq_total = mcqCount;
    }

    // Track page visit
    if (!this.sessionData.navigation.pages_visited.includes(mode)) {
      this.sessionData.navigation.pages_visited.push(mode);
    }
  }

  // Track question interactions
  trackQuestionAnswered(questionData) {
    this.sessionData.demo_completion.questions_answered++;
    
    // Update completion rate
    if (this.sessionData.demo_completion.total_questions > 0) {
      this.sessionData.demo_completion.completion_rate = 
        this.sessionData.demo_completion.questions_answered / 
        this.sessionData.demo_completion.total_questions;
    }

    // Track MCQ performance
    if (questionData.type === 'mcq' && questionData.isCorrect !== undefined) {
      if (questionData.isCorrect) {
        this.sessionData.performance.mcq_correct++;
      }
      
      // Update MCQ accuracy
      const answeredMcq = this.sessionData.performance.mcq_correct + 
        (this.sessionData.demo_completion.questions_answered - this.sessionData.performance.mcq_correct);
      
      if (answeredMcq > 0) {
        this.sessionData.performance.mcq_accuracy = 
          this.sessionData.performance.mcq_correct / answeredMcq;
      }
    }

    // Track extended questions
    if (questionData.type === 'extended') {
      this.sessionData.performance.extended_questions_viewed++;
    }
  }

  // Track help-seeking behavior
  trackHintClick(questionNumber) {
    this.sessionData.interactions.hint_clicks++;
    
    if (!this.sessionData.interactions.help_seeking_questions.includes(questionNumber)) {
      this.sessionData.interactions.help_seeking_questions.push(questionNumber);
    }
    
    if (!this.sessionData.performance.questions_with_help.includes(questionNumber)) {
      this.sessionData.performance.questions_with_help.push(questionNumber);
    }
  }

  trackAIChatClick(questionNumber) {
    this.sessionData.interactions.ai_chat_clicks++;
    
    if (!this.sessionData.interactions.help_seeking_questions.includes(questionNumber)) {
      this.sessionData.interactions.help_seeking_questions.push(questionNumber);
    }
    
    if (!this.sessionData.performance.questions_with_help.includes(questionNumber)) {
      this.sessionData.performance.questions_with_help.push(questionNumber);
    }
  }

  // Track CTA interactions
  trackCTAClick(ctaType) {
    if (this.sessionData.interactions.cta_clicks[ctaType] !== undefined) {
      this.sessionData.interactions.cta_clicks[ctaType]++;
    }
  }

  // Track quiz completion with final score
  trackQuizCompletion(finalScore) {
    this.sessionData.demo_completion.final_score = finalScore;
  }

  // Track navigation events
  trackNavigationEvent(event, data = {}) {
    this.sessionData.interactions.navigation_events.push({
      event,
      timestamp: Date.now() - this.startTime,
      ...data
    });
  }

  // Finalize session data for signup
  getSignupAnalytics() {
    // Calculate total time spent
    this.sessionData.demo_completion.time_spent_seconds = 
      Math.round((Date.now() - this.startTime) / 1000);
    
    // Set time to signup
    this.sessionData.navigation.time_to_signup_seconds = 
      this.sessionData.demo_completion.time_spent_seconds;

    // Clean up and return final data
    return {
      ...this.sessionData,
      collected_at: new Date().toISOString(),
      session_id: this.generateSessionId()
    };
  }

  // Generate unique session identifier
  generateSessionId() {
    return `demo_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  // Get summary for quick insights
  getSummary() {
    const data = this.sessionData;
    return {
      engagement_level: this.calculateEngagementLevel(),
      help_seeking_ratio: data.interactions.hint_clicks + data.interactions.ai_chat_clicks > 0,
      completion_rate: data.demo_completion.completion_rate,
      performance_level: data.performance.mcq_accuracy,
      total_interactions: Object.values(data.interactions.cta_clicks).reduce((a, b) => a + b, 0) + 
                         data.interactions.hint_clicks + data.interactions.ai_chat_clicks
    };
  }

  calculateEngagementLevel() {
    const completion = this.sessionData.demo_completion.completion_rate;
    const interactions = this.sessionData.interactions.hint_clicks + 
                        this.sessionData.interactions.ai_chat_clicks +
                        Object.values(this.sessionData.interactions.cta_clicks).reduce((a, b) => a + b, 0);
    
    if (completion >= 0.8 && interactions >= 3) return 'high';
    if (completion >= 0.5 && interactions >= 2) return 'medium';
    return 'low';
  }

  // Reset session (for new demo attempt)
  reset() {
    this.sessionData = this.initializeSession();
    this.startTime = Date.now();
  }
}

// Create global instance
const demoAnalytics = new DemoAnalyticsService();

export default demoAnalytics;